import React, { useEffect, useRef, useState } from "react";
import { BarTask } from "../../types/bar-task";
import { Task } from "../../types/public-types";
import { GanttContentMoveAction } from "../../types/gantt-task-actions";
import { Bar } from "./bar/bar";
import { BarSmall } from "./bar/bar-small";
import { Milestone } from "./milestone/milestone";
import { Project } from "./project/project";
import { getContrastColor } from "../../helpers/color-contrast";
import style from "./task-list.module.css";

export type TaskItemProps = {
  task: BarTask;
  arrowIndent: number;
  taskHeight: number;
  isProgressChangeable: boolean;
  isDateChangeable: boolean;
  isDelete: boolean;
  isSelected: boolean;
  rtl: boolean;
  onEventStart: (
    action: GanttContentMoveAction,
    selectedTask: BarTask,
    event?: React.MouseEvent | React.KeyboardEvent
  ) => any;
  allTasks?: Task[];
  collapsedChains?: Set<string>;
  dependencyChains?: Map<string, string[]>;
  isCollapsedChainParent?: boolean;
  chainTasks?: Task[];
};

export const TaskItem: React.FC<TaskItemProps> = props => {
  const {
    task,
    arrowIndent,
    isDelete,
    taskHeight,
    isSelected,
    rtl,
    onEventStart,
    allTasks,
    collapsedChains,
    dependencyChains,
  } = {
    ...props,
  };

  const textRef = useRef<SVGTextElement>(null);
  const [taskItem, setTaskItem] = useState<JSX.Element>(<div />);
  const [isTextInside, setIsTextInside] = useState(true);
  const [textColor, setTextColor] = useState<string>('white');
  const [isCollapsedChainParent, setIsCollapsedChainParent] = useState(false);

  useEffect(() => {
    // Determine text color based on background
    // The backgroundColor from GanttChart.js becomes part of task and gets mapped to styles.backgroundColor
    const backgroundColor = task.styles?.backgroundColor || '#3182ce';
    const textColor = getContrastColor(backgroundColor);
    setTextColor(textColor);
  }, [task.styles]);

  useEffect(() => {
    // Check if this is a collapsed dependency chain parent
    const isCollapsedChainParentNow = !!(
      task.isChainParent &&
      task.dependencyChain &&
      collapsedChains?.has(task.id) &&
      dependencyChains?.has(task.id)
    );

    setIsCollapsedChainParent(isCollapsedChainParentNow);

    // Get the tasks in the dependency chain if collapsed
    const chainTaskIdsNow = dependencyChains?.get(task.id) || [];
    const chainTasksNow = isCollapsedChainParentNow && allTasks
      ? chainTaskIdsNow
          .map(id => allTasks.find(t => t.id === id))
          .filter((t): t is Task => t !== undefined)
      : [];

    // For collapsed chain parents, always use Bar (which will render as collapsed)
    if (isCollapsedChainParentNow && chainTasksNow.length > 1) {
      setTaskItem(<Bar {...props} isCollapsedChainParent={true} chainTasks={chainTasksNow} />);
    } else {
      switch (task.typeInternal) {
        case "milestone":
          setTaskItem(<Milestone {...props} />);
          break;
        case "project":
          setTaskItem(<Project {...props} />);
          break;
        case "smalltask":
          setTaskItem(<BarSmall {...props} />);
          break;
        default:
          setTaskItem(<Bar {...props} isCollapsedChainParent={false} chainTasks={[]} />);
          break;
      }
    }
  }, [task, isSelected, task.isChainParent, task.dependencyChain, task.id, task.typeInternal, allTasks, collapsedChains, dependencyChains]);

  useEffect(() => {
    if (textRef.current) {
      setIsTextInside(textRef.current.getBBox().width < task.x2 - task.x1 - 30);
    }
  }, [task.x1, task.x2]);

  const getX = () => {
    const width = task.x2 - task.x1;
    const hasChild = task.barChildren.length > 0;
    if (isTextInside) {
      return task.x1 + width * 0.5;
    }
    if (rtl && textRef.current) {
      return (
        task.x1 -
        textRef.current.getBBox().width -
        arrowIndent * +hasChild -
        arrowIndent * 0.2
      );
    } else {
      return task.x1 + width + arrowIndent * +hasChild + arrowIndent * 0.2;
    }
  };

  return (
    <g
      onKeyDown={e => {
        switch (e.key) {
          case "Delete": {
            if (isDelete) onEventStart("delete", task, e);
            break;
          }
        }
        e.stopPropagation();
      }}
      onMouseEnter={e => {
        onEventStart("mouseenter", task, e);
      }}
      onMouseLeave={e => {
        onEventStart("mouseleave", task, e);
      }}
      onDoubleClick={e => {
        onEventStart("dblclick", task, e);
      }}
      onClick={e => {
        onEventStart("click", task, e);
      }}
      onFocus={() => {
        onEventStart("select", task);
      }}
      onContextMenu={e => {
        e.preventDefault();
        if (e.type === "contextmenu") {
          onEventStart("rightclick", task, e);
        }
      }}
    >
      {taskItem}
      {!isCollapsedChainParent && (
        <text
          x={getX()}
          y={task.y + taskHeight * 0.5}
          className={
            isTextInside
              ? style.barLabel
              : style.barLabel && style.barLabelOutside
          }
          ref={textRef}
          fontStyle={task.name ? "normal" : "italic"}
          style={isTextInside ? { fill: textColor } : undefined}
        >
          {task.name || '(Bez nazwy)'}
        </text>
      )}
    </g>
  );
};
